/**
 * we have quite a few maps that are keyed on chatId
 * These are usually Record<string, T>
 * But that doesn't work with ChatIdentifier
 *  */
import { defaultDeserialiser, defaultSerialiser, } from "../domain";
export class SafeMap {
    #isPrimitive;
    #serialise;
    #deserialise;
    // the original key is stored alongside the value so that iteration never
    // needs to deserialise it
    #map;
    #newMap() {
        return this.#isPrimitive
            ? new SafeMap(undefined, undefined)
            : new SafeMap(this.#serialise, this.#deserialise);
    }
    constructor(serialiser, deserialiser, map) {
        this.#isPrimitive = serialiser === undefined && deserialiser === undefined;
        this.#serialise = serialiser ?? defaultSerialiser;
        this.#deserialise = deserialiser ?? defaultDeserialiser;
        this.#map = new Map();
        if (map !== undefined) {
            for (const [k, v] of map.entries()) {
                this.#map.set(k, [this.#deserialise(k), v]);
            }
        }
    }
    [Symbol.iterator]() {
        return this.entries();
    }
    map(fn) {
        const mapped = this.#newMap();
        for (const [k, v] of this.entries()) {
            mapped.set(k, fn(k, v));
        }
        return mapped;
    }
    merge(other) {
        other.forEach((val, key) => {
            this.set(key, val);
        });
        return this;
    }
    filter(fn) {
        return [...this.entries()]
            .filter(([k, v]) => {
            return fn(v, k);
        })
            .reduce((agg, [k, v]) => {
            agg.set(k, v);
            return agg;
        }, this.#newMap());
    }
    reduce(reducer, initialValue) {
        let acc = initialValue;
        for (const entry of this) {
            acc = reducer(acc, entry, this);
        }
        return acc;
    }
    clone() {
        const cloned = this.#newMap();
        for (const [key, value] of this) {
            cloned.set(key, value);
        }
        return cloned;
    }
    empty() {
        return this.#newMap();
    }
    clear() {
        this.#map.clear();
    }
    values() {
        const it = this.#map.values();
        return {
            [Symbol.iterator]() {
                return this;
            },
            next() {
                const result = it.next();
                if (result.done)
                    return { done: true, value: undefined };
                return { done: false, value: result.value[1] };
            },
        };
    }
    keys() {
        const it = this.#map.values();
        return {
            [Symbol.iterator]() {
                return this;
            },
            next() {
                const result = it.next();
                if (result.done)
                    return { done: true, value: undefined };
                return { done: false, value: result.value[0] };
            },
        };
    }
    entries() {
        const it = this.#map.values();
        return {
            [Symbol.iterator]() {
                return this;
            },
            next() {
                const result = it.next();
                if (result.done)
                    return { done: true, value: undefined };
                const [key, value] = result.value;
                return { done: false, value: [key, value] };
            },
        };
    }
    delete(key) {
        if (this.#map.size === 0)
            return false;
        return this.#map.delete(this.#serialise(key));
    }
    forEach(callbackfn) {
        for (const [key, value] of this.#map.values()) {
            callbackfn(value, key, this);
        }
    }
    get(key) {
        if (this.#map.size === 0)
            return undefined;
        return this.#map.get(this.#serialise(key))?.[1];
    }
    has(key) {
        if (this.#map.size === 0)
            return false;
        return this.#map.has(this.#serialise(key));
    }
    set(key, value) {
        this.#map.set(this.#serialise(key), [key, value]);
        return this;
    }
    get size() {
        return this.#map.size;
    }
    toMap() {
        const map = new Map();
        for (const [k, [, v]] of this.#map.entries()) {
            map.set(k, v);
        }
        return map;
    }
    static fromEntries(entries, serialiser, deserialiser) {
        const map = new SafeMap(serialiser, deserialiser);
        for (const [k, v] of entries) {
            map.set(k, v);
        }
        return map;
    }
}
// Hand-rolled key codecs: much cheaper than JSON.stringify/parse and injective
// across identifier kinds (ids are principals / numbers so never contain "|")
export function chatIdentifierToKey(id) {
    switch (id.kind) {
        case "direct_chat":
            return "d|" + id.userId;
        case "group_chat":
            return "g|" + id.groupId;
        case "channel":
            return "c|" + id.communityId + "|" + id.channelId;
        default:
            throw new Error(`Unknown chat identifier kind: ${JSON.stringify(id)}`);
    }
}
export function chatIdentifierFromKey(key) {
    switch (key[0]) {
        case "d":
            return { kind: "direct_chat", userId: key.slice(2) };
        case "g":
            return { kind: "group_chat", groupId: key.slice(2) };
        case "c": {
            const i = key.lastIndexOf("|");
            return {
                kind: "channel",
                communityId: key.slice(2, i),
                channelId: Number(key.slice(i + 1)),
            };
        }
        default:
            throw new Error(`Invalid chat identifier key: ${key}`);
    }
}
export function messageContextToKey(ctx) {
    return chatIdentifierToKey(ctx.chatId) + "|" + (ctx.threadRootMessageIndex ?? "");
}
export function messageContextFromKey(key) {
    const i = key.lastIndexOf("|");
    const chatId = chatIdentifierFromKey(key.slice(0, i));
    const thread = key.slice(i + 1);
    return thread === "" ? { chatId } : { chatId, threadRootMessageIndex: Number(thread) };
}
export function chatListScopeToKey(scope) {
    return scope.kind === "community" ? "community|" + scope.id.communityId : scope.kind;
}
export function chatListScopeFromKey(key) {
    if (key.startsWith("community|")) {
        return { kind: "community", id: { kind: "community", communityId: key.slice(10) } };
    }
    return { kind: key };
}
// This is a bit weird
export class GlobalMap extends SafeMap {
    constructor(_map) {
        super((_) => "global", (_) => "global", _map);
    }
}
export class ChatMap extends SafeMap {
    constructor(_map) {
        super((k) => chatIdentifierToKey(k), (k) => chatIdentifierFromKey(String(k)), _map);
    }
    static fromList(things) {
        return things.reduce((map, c) => {
            map.set(c.id, c);
            return map;
        }, new ChatMap());
    }
    static fromMap(map) {
        return new ChatMap(map);
    }
    static fromJSON(json) {
        return new ChatMap(new Map(JSON.parse(json)));
    }
}
export class MessageContextMap extends SafeMap {
    constructor(_map) {
        super((k) => messageContextToKey(k), (k) => messageContextFromKey(String(k)), _map);
    }
    static fromMap(map) {
        return new MessageContextMap(map);
    }
}
export class ChatListScopeMap extends SafeMap {
    constructor(_map) {
        super((k) => chatListScopeToKey(k), (k) => chatListScopeFromKey(String(k)), _map);
    }
}
export class CommunityMap extends SafeMap {
    constructor() {
        super((k) => k.communityId, (k) => ({ kind: "community", communityId: String(k) }));
    }
    static fromList(things) {
        return things.reduce((map, c) => {
            map.set(c.id, c);
            return map;
        }, new CommunityMap());
    }
}
export class MessageMap extends SafeMap {
    constructor(entries) {
        super((k) => k.toString(), (k) => BigInt(k));
        if (entries !== undefined) {
            for (const [k, v] of entries) {
                this.set(k, v);
            }
        }
    }
}
export function getOrAdd(map, key, value) {
    const existing = map.get(key);
    if (existing !== undefined) {
        return existing;
    }
    map.set(key, value);
    return value;
}
